import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useHelp } from "@/context/HelpContext";
import { helpForRoles, type HelpArticle } from "@/lib/help";
import { news, newsDate, type NewsRelease } from "@/lib/news";
import { HelpPanel, HelpSections } from "./HelpPanel";
import { Button } from "./ui/button";
import "./News.css";

function useNewsHelp() {
  const { articles } = useHelp();
  const { user } = useAuth();
  return helpForRoles(articles, user?.roles.map((role) => role.code) ?? []);
}

export function NewsCards({
  release,
  onHelp,
}: {
  release: NewsRelease;
  onHelp: (article: HelpArticle) => void;
}) {
  const articles = useNewsHelp();
  return (
    <div className="help-sections">
      {release.items.map((item, index) => {
        const article = articles.find(
          (article) => article.screen === item.help_screen,
        );
        return (
          <section className="help-card news-card" key={index}>
            <h3>
              <span aria-hidden="true">{item.icon}</span> {item.title}
            </h3>
            <p>{item.sentence}</p>
            {article && (
              <Button variant="secondary" onClick={() => onHelp(article)}>
                Pokaži kako
              </Button>
            )}
          </section>
        );
      })}
    </div>
  );
}

export function NewsSheet({
  release,
  onSeen,
}: {
  release: NewsRelease;
  onSeen: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const saving = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [article, setArticle] = useState<HelpArticle | null>(null);
  const title = `Novo u aplikaciji · ${newsDate(release.date)}`;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const element = dialog.current;
    element?.showModal();
    return () => {
      element?.close();
      previous?.focus();
    };
  }, []);

  async function acknowledge() {
    if (saving.current) return;
    saving.current = true;
    setBusy(true);
    setError("");
    try {
      await news.seen(release.date);
      onSeen();
    } catch {
      setError("Potvrda nije spremljena. Pokušajte ponovno.");
    } finally {
      saving.current = false;
      setBusy(false);
    }
  }

  return (
    <dialog
      ref={dialog}
      className="help-dialog news-dialog"
      aria-label={title}
      onCancel={(event) => event.preventDefault()}
    >
      <div className="help-panel">
        <div className="help-grab" aria-hidden="true" />
        <header className="help-panel-heading">
          <h2>{article ? article.title : title}</h2>
        </header>
        {article ? (
          <>
            <HelpSections article={article} />
            <Button variant="secondary" onClick={() => setArticle(null)}>
              Natrag na novosti
            </Button>
          </>
        ) : (
          <>
            <NewsCards release={release} onHelp={setArticle} />
            {error && <p role="alert">{error}</p>}
            <div className="help-actions">
              <Button disabled={busy} onClick={() => void acknowledge()}>
                {busy ? "Spremanje…" : "U redu"}
              </Button>
            </div>
            <p className="help-muted">
              Prikazuje se jednom; samo novosti za tvoju ulogu. Kasnije u Pomoć
              → Što je novo.
            </p>
          </>
        )}
      </div>
    </dialog>
  );
}

export function NewsEntry() {
  const [release, setRelease] = useState<NewsRelease | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    void news
      .load(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setRelease(data.unseen);
      })
      .catch(() => {
        // News must not prevent using the application when the network is down.
      });
    return () => controller.abort();
  }, []);
  return release ? (
    <NewsSheet release={release} onSeen={() => setRelease(null)} />
  ) : null;
}

export function NewsArchive({ releases }: { releases: NewsRelease[] }) {
  const [article, setArticle] = useState<HelpArticle | null>(null);
  return (
    <>
      <Link className="help-link-button" to="/pomoc/upute">
        Sve upute
      </Link>
      {!releases.length && <p>Još nema novosti za tvoju ulogu.</p>}
      {releases.map((release) => (
        <section className="help-card" key={release.date}>
          <h2>Novo u aplikaciji · {newsDate(release.date)}</h2>
          <NewsCards release={release} onHelp={setArticle} />
        </section>
      ))}
      {article && (
        <HelpPanel article={article} onClose={() => setArticle(null)} />
      )}
    </>
  );
}
